import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import DocumentUploadModal from "../DocumentUploadModal";
import DocumentViewModal from "../../../../Components/DocumentViewModal";
import NoDataCreated from "../../../../Components/NoDataCreated";
import ErrorPage from "../../../../Components/ErrorPage";
import PageLoader from "../../../../Components/PageLoader";
import { goToTop } from "../../../../Components/goToTop";
import { format } from "date-fns";

import { checkFileOthers, checkText } from "../../../../Utils/Validator";
import {
  serviceTypeDocumentCreateAPI,
  serviceTypeDocumentDeleteAPI,
  serviceTypeDocumentGetDataAPI,
  serviceTypeDocumentUpdateAPI,
} from "../../../../Api/Project/ServiceTypeDocument";
import { DocumentInterface } from "../../../../Controllers/Types/Project/Service/DocumentInterface";
import TableActionLoader from "../../../../Components/TableActionLoader";

const Document: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectData = location.state?.data;
  const serviceId = projectData.folder_id;

  const toast = useRef<Toast>(null);
  const [dataloading, setDataLoading] = useState<boolean>(false);
  const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // const isShared: boolean = projectData.sharedBy !== null ? true : false;
  const isShared: boolean = projectData.can_edit === false ? true : false;

  const [dataState, setDataState] = useState<"Add" | "Edit">("Add");
  const [loading, setLoading] = useState<boolean>(false);
  const [showDocumentViewModal, setShowDocumentViewModal] = useState(false);
  const [documentList, setDocumentList] = useState<DocumentInterface[]>([]);
  const [allDocumentPath, setAllDocumentPath] = useState<string[]>([]);
  const [documentPath, setDocumentPath] = useState<string>("");
  const [selectedDocument, setSelectedDocument] = useState<DocumentInterface | null>(null);
  const [deleteLoadingId, setdeleteLoadingId] = useState<number | null>(null);

  /* ****************************** For Document upload ****************************** */
  const [isModalOpen, setModalOpen] = useState(false);
  const [documentName, setDocumentName] = useState<string>("");
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  /* error massage */
  const [errorDocumentName, setErrorDocumentName] = useState<string>("");
  const [errorUploadedFile, setErrorUploadedFile] = useState<string>("");

  const [documentUpdateHasChanges, setDocumentUpdateHasChanges] = useState<boolean>(false); 

  const handleOpenCreateModal = () => {
    setModalOpen(true);
    setDataState("Add");
  };

  const handleClearFields = () => {
    setDocumentName('');
    setUploadedFileName("");
    setUploadedFile(null);

    setSelectedDocument(null);
  };

  const handleClearFile = () => {
    setUploadedFileName("");
    setUploadedFile(null);
  }

  const handleClearErrors = () => {
    setErrorDocumentName("");
    setErrorUploadedFile("");
  };

  const handleOpenEditModal = (document: DocumentInterface) => {
    setDataState("Edit");
    setModalOpen(true);

    setSelectedDocument(document);
    setDocumentName(document?.title);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    handleClearFields();
    handleClearErrors();
  };

  const handleFileUpload = (file: File) => {
    setUploadedFileName(file.name);
    setUploadedFile(file);
  };

  /* ************************************************************************************************************************************************************ */
  useEffect(() => {
    documentDataGet();
  }, []);


  useEffect(() => {
    const allpath = documentList.map((e) => e.path);
    setAllDocumentPath(allpath);
  }, [documentList]);



  /* *********************************************** get document data list *********************************************************************** */
  const documentDataGet = async () => {
    if (serviceId) {
      setDataLoading(true);
      const data = await serviceTypeDocumentGetDataAPI(Number(serviceId));
      setDataLoading(false);
      setInitialDataLoading(false);
      if (data.status) {
        setDocumentList(data.data || []);
      } else {
        setErrorMessage('An error occurred while retrieving document data.');
      }
    }
  };
  /* *********************************************** get document data list *********************************************************************** */
  /* ************************************************************************************************************************************************************ */

  /* *******************************************  create document - start ******************************************************** */
  useEffect(() => {
    checkText(documentName, false, setErrorDocumentName);
  }, [documentName])

  useEffect(() => {
    checkFileOthers(uploadedFile!, false, setErrorUploadedFile);
  }, [uploadedFile])

  function checkCredentials(fileRequired: boolean): boolean {
    const isDocumentNameValid = checkText(
      documentName,
      true,
      setErrorDocumentName
    );
    const isUploadedFileValid = checkFileOthers(
      uploadedFile!,
      fileRequired,
      setErrorUploadedFile
    );

    return isDocumentNameValid && isUploadedFileValid;
  }

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials(true)) {
      setLoading(true);
      const data = await serviceTypeDocumentCreateAPI(
        Number(projectData.project_id),
        Number(serviceId),
        documentName,
        uploadedFile!
      );
      setLoading(false);

      if (data.statusCode === 200) {
        documentDataGet();

        handleCloseModal();

        if (toast.current) {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `Document added successfully.`,
            life: 3000,
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error uploading document.",
            detail: "There was an error uploading document.",
            life: 3000,
          });
        }
      }
    } else {
    }
  };
  /* *******************************************  create document - end ******************************************************** */

  useEffect(() => {
    if (selectedDocument) {
      if (documentName !== selectedDocument?.title) {
        setDocumentUpdateHasChanges(true);
      } else if (uploadedFile !== null) {
        setDocumentUpdateHasChanges(true);
      } else {
        setDocumentUpdateHasChanges(false);
      }
    }
  }, [selectedDocument, documentName, uploadedFile]);


  /* *******************************************  update document - start ******************************************************** */
  const handleUpdateDocument = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials(false)) {
      setLoading(true);
      const data = await serviceTypeDocumentUpdateAPI(
        selectedDocument?.document_id || 0,
        documentName,
        uploadedFile!
      );
      setLoading(false);

      if (data.statusCode === 200) {
        documentDataGet();

        handleCloseModal();

        if (toast.current) {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `Document updated successfully.`,
            life: 3000,
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating document.",
            life: 3000,
          });
        }
      }
    }
  };
  /* *******************************************  update document - end ******************************************************** */

  /* *******************************************  delete document - start ******************************************************** */
  const handleDeletedocumentData = (documentId: number): void => {
    confirmDialog({
      message: "Are you sure you want to delete Document detail?",
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteDocumentData(documentId),
      resizable: false,
      draggable: false,
      dismissableMask: true,
    });
  };

  // delete operation
  const deleteDocumentData = async (documentId: number) => {
    setdeleteLoadingId(documentId);
    const data = await serviceTypeDocumentDeleteAPI(documentId);

    setdeleteLoadingId(null);
    if (data.statusCode === 200) {
      documentDataGet();
      handleCloseModal();

      if (toast.current) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: `Document deleted successfully.`,
          life: 3000,
        });
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error deleting document.",
          detail: "There was an error deleting document.",
          life: 3000,
        });
      }
    }
  };
  /* *******************************************  delete document - end ******************************************************** */

  /* ***************************** For show ducument data **************************** */

  /* Table structure */
  const editDataBody = (rowData: DocumentInterface) => {
    const document = rowData;

    return (
      <button
        className="data_action_btn primary_table_btn p-ripple"
        onClick={() => handleOpenEditModal(document)}
      >
        <i className="bi bi-pencil-square"></i>
        <Ripple />
      </button>
    );
  };

  const deleteDataBody = (rowData: DocumentInterface) => {
    const documentId = rowData.document_id;
    return (
      <React.Fragment>
        {deleteLoadingId === documentId ? (
          <TableActionLoader />
        ) : (
          <button
            className="data_action_btn danger_table_btn p-ripple"
            onClick={() => handleDeletedocumentData(documentId)}
          >
            <i className="bi bi-trash3"></i>
            <Ripple />
          </button>
        )}
      </React.Fragment>
    );
  };

  const viewDocumentBody = (rowData: DocumentInterface) => {
    const documentPath = rowData.path;
    return (
      <div className="tabel_btn_area justify-content-end justify-content-lg-center mt-1 mb-1">
        <Button
          label="View"
          className="tabel_btn view_btn"
          onClick={() => handleOpenDocumentViewModal(documentPath)}
        />
      </div>
    );
  };
  /* ********************************************************************************* */

  /* ******************************* For view document ******************************* */
  const handleOpenDocumentViewModal = (path: string) => {
    setDocumentPath(path);
    setShowDocumentViewModal(true);
  };

  const handleCloseDocumentViewModal = () => {
    setShowDocumentViewModal(false);
  };
  /* ********************************************************************************* */
  const goBack = () => {
    navigate(-1);
    goToTop();
  };

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {dataloading && initialDataloading ? (
          <PageLoader />
        ) : errorMessage ? (
          <ErrorPage errorMessage={errorMessage} />
        ) : (
          <div>
            <div className="page_content mt-0">
              <div className="page_breadcrumb_area">
                <ul className="page_breadcrumb">
                  <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                    {projectData.job_number} : {projectData.address}
                  </li>
                  <li className="page_breadcrumb_item has_child">
                    {projectData.folder}
                  </li>
                  <li className="page_breadcrumb_item active">
                    {projectData.service_name}
                  </li>
                </ul>
                <button className="back_btn p-ripple" onClick={goBack}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                  <Ripple />
                </button>
              </div>

              <div className="page_header_area mt-sm-2 justify-content-end">
                <div className="page_action_area">
                  {!isShared && (
                    <button
                      className="add_data_link_btn p-ripple"
                      onClick={handleOpenCreateModal}
                    >
                      <i className="bi bi-plus-circle"></i>
                      <span>Add document</span>
                      <Ripple />
                    </button>
                  )}
                </div>
              </div>

              <div className="page_main_content_section">
                {documentList?.length > 0 ? (
                  <DataTable
                    value={documentList}
                    paginator
                    size="small"
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    tableStyle={{ minWidth: "50rem" }}
                    paginatorDropdownAppendTo={"self"}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    className="page_table p-0 p-sm-2"
                    rowHover
                    loading={dataloading}
                    scrollable
                    dataKey="document_id"
                  >
                    <Column
                      header="Document name"
                      headerClassName="text_purple"
                      field="title"
                      body={(rowData: DocumentInterface) => (
                        <span>{rowData?.title ? rowData?.title : "---"}</span>
                      )}
                      style={{ width: "30%" }}
                    ></Column>

                    <Column
                      header="Uploaded date/time"
                      headerClassName="text_purple"
                      field="update_at"
                      body={(rowData: DocumentInterface) => (
                        <span className="text_no_wrap">
                          {rowData?.created_datatime
                            ? format(
                              new Date(rowData.created_datatime),
                              "yyyy-MM-dd | hh:mm a"
                            )
                            : "---"}
                        </span>
                      )}
                      style={{ width: "20%" }}
                    ></Column>

                    {!isShared && [
                      <Column
                        header="Edit"
                        headerClassName="text_purple"
                        body={editDataBody}
                        alignHeader="center"
                        className="data_action_btn_area"
                        style={{ width: "15%" }}
                      ></Column>,

                      <Column
                        header="Delete"
                        headerClassName="text_purple"
                        body={deleteDataBody}
                        alignHeader="center"
                        className="data_action_btn_area"
                        style={{ width: "15%" }}
                      ></Column>,
                    ]}

                    <Column
                      body={viewDocumentBody}
                      className="table_btn_area"
                      style={{ width: "20%" }}
                      frozen
                      alignFrozen="right"
                    ></Column>
                  </DataTable>
                ) : (
                  <NoDataCreated
                    message={`No documents added. ${!isShared ? 'To add the document click on add button' : ''}`}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Document upload modal */}
        <DocumentUploadModal
          isOpen={isModalOpen}
          dataState={dataState}
          onClose={handleCloseModal}
          serviceName={projectData.service_name}
          toast={toast}
          documentName={documentName}
          setDocumentName={setDocumentName}
          uploadedFileName={uploadedFileName}
          handleFileUpload={handleFileUpload}
          handleCreateDocument={handleCreateDocument}
          handleUpdateDocument={handleUpdateDocument}
          loading={loading}
          documentNameError={errorDocumentName}
          fileError={errorUploadedFile}
          labelName="Document name"
          headerName="document"
          isFileRequired={dataState === "Add" ? true : false}
          isFileNameRequired={true}
          handleClearFile={handleClearFile}
          documentUpdateHasChanges={documentUpdateHasChanges}
        />

        {/* Document view modal */}
        <DocumentViewModal
          allFileUrls={allDocumentPath}
          isOpen={showDocumentViewModal}
          onClose={handleCloseDocumentViewModal}
          fileUrl={documentPath}
        />
      </div>
    </React.Fragment>
  );
};

export default Document;
